import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Eye, EyeOff, Lock, User, Mail, ArrowRight, Shield, UserPlus, AlertCircle, Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const Login = () => {
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get('invite');

  // Invite validation state
  const [inviteData, setInviteData] = useState(null);
  const [inviteValidating, setInviteValidating] = useState(!!inviteToken);
  const [inviteError, setInviteError] = useState('');

  // Whether we're in signup mode (only possible via valid invite link)
  const isSignUp = !!inviteData;

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    studentId: '',
    program: '',
    graduationYear: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState('');
  const [showProfileForm, setShowProfileForm] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const { login, signupWithInvite, createMemberProfile, resendConfirmation, isLoading, isAuthenticated, validateInvite } = useAuth();

  const from = location.state?.from?.pathname || '/dashboard';

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, from]);

  // Validate invite token on mount
  useEffect(() => {
    if (!inviteToken) return;

    const validate = async () => {
      setInviteValidating(true);
      const result = await validateInvite(inviteToken);
      if (result.valid) {
        setInviteData(result.invite);
        setFormData(prev => ({ ...prev, email: result.invite.email }));
      } else {
        setInviteError(result.error);
      }
      setInviteValidating(false);
    };

    validate();
  }, [inviteToken]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleResendConfirmation = async () => {
    const result = await resendConfirmation(formData.email);
    if (result.success) {
      setSuccess('Confirmation email sent! Please check your inbox.');
    } else {
      setErrors({ general: result.error });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    if (isSignUp) {
      if (!formData.firstName) newErrors.firstName = 'First name is required';
      if (!formData.lastName) newErrors.lastName = 'Last name is required';
      if (!formData.confirmPassword) {
        newErrors.confirmPassword = 'Please confirm your password';
      } else if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
    }

    if (showProfileForm) {
      if (!formData.studentId) newErrors.studentId = 'Student ID is required';
      if (!formData.program) newErrors.program = 'Program is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      if (showProfileForm) {
        const result = await createMemberProfile({
          firstName: formData.firstName,
          lastName: formData.lastName,
          studentId: formData.studentId,
          program: formData.program,
          graduationYear: formData.graduationYear ? parseInt(formData.graduationYear) : null,
          role: inviteData?.role || 'executive'
        });

        if (result.success) {
          setSuccess('Profile created successfully! Redirecting to dashboard...');
          setTimeout(() => navigate(from, { replace: true }), 2000);
        } else {
          setErrors({ general: result.error || 'Failed to create profile' });
        }

      } else if (isSignUp) {
        const result = await signupWithInvite(inviteToken, formData.email, formData.password, {
          firstName: formData.firstName,
          lastName: formData.lastName,
        });

        if (result.success) {
          if (result.needsEmailConfirmation) {
            setSuccess('Please check your email to confirm your account before signing in.');
          } else if (result.profileCreated) {
            setSuccess('Account created! Redirecting to dashboard...');
            setTimeout(() => navigate(from, { replace: true }), 2000);
          } else {
            setShowProfileForm(true);
          }
        } else {
          setErrors({ general: result.error || 'Signup failed' });
        }

      } else {
        const result = await login(formData.email, formData.password);
        if (result.success) {
          navigate(from, { replace: true });
        } else {
          setErrors({ general: result.error || 'Login failed. Please check your credentials.' });
        }
      }
    } catch {
      setErrors({ general: isSignUp ? 'Signup failed. Please try again.' : 'Login failed. Please check your credentials.' });
    }
  };

  const roleLabel = {
    admin: 'Admin',
    executive: 'Executive',
    member: 'Member',
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0395fc] to-[#0070c0] flex items-center justify-center p-4 relative overflow-hidden">
      {/* Background blobs */}
      <motion.div
        className="absolute top-20 right-20 w-64 h-64 bg-white/10 rounded-full blur-3xl"
        animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.6, 0.3] }}
        transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
      />
      <motion.div
        className="absolute bottom-20 left-20 w-80 h-80 bg-white/5 rounded-full blur-3xl"
        animate={{ scale: [1, 1.3, 1], opacity: [0.2, 0.5, 0.2] }}
        transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut', delay: 2 }}
      />

      <motion.div
        initial={{ opacity: 0, y: 50, scale: 0.9 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.8, ease: 'easeOut' }}
        className="bg-white rounded-lg border-4 border-black shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] w-full max-w-md p-8 relative z-10"
      >
        {/* Header */}
        <div className="text-center mb-8">
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="flex items-center justify-center mb-6"
          >
            <div className="w-16 h-16 bg-[#0395fc] rounded-full flex items-center justify-center border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
              <Shield className="w-8 h-8 text-white" />
            </div>
          </motion.div>

          <h1 className="text-3xl font-black font-[var(--font-anton)] text-foreground mb-2">
            {showProfileForm ? 'COMPLETE PROFILE' : isSignUp ? 'CREATE ACCOUNT' : 'DMLS DASHBOARD'}
          </h1>
          <p className="text-muted-foreground font-[var(--font-dm-sans)]">
            {showProfileForm
              ? 'Complete your member profile to access the dashboard'
              : isSignUp
              ? `You've been invited as ${roleLabel[inviteData?.role] || 'a member'}`
              : 'Sign in to access your council portal'}
          </p>
        </div>

        {/* Invite validating spinner */}
        {inviteValidating && (
          <div className="flex flex-col items-center py-8 gap-3">
            <Loader2 className="w-8 h-8 animate-spin text-[#0395fc]" />
            <p className="text-sm text-muted-foreground font-[var(--font-dm-sans)]">Validating invite...</p>
          </div>
        )}

        {/* Invalid invite error */}
        {!inviteValidating && inviteToken && !inviteData && inviteError && (
          <div className="flex flex-col items-center py-6 gap-4">
            <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center border-2 border-red-400">
              <AlertCircle className="w-7 h-7 text-red-500" />
            </div>
            <div className="text-center">
              <p className="font-bold text-foreground font-[var(--font-dm-sans)] mb-1">Invalid Invite</p>
              <p className="text-sm text-red-600 font-[var(--font-dm-sans)]">{inviteError}</p>
            </div>
            <button
              type="button"
              onClick={() => navigate('/login')}
              className="text-sm text-[#0395fc] hover:underline font-[var(--font-dm-sans)]"
            >
              Go to login
            </button>
          </div>
        )}

        {/* Main form — shown unless we're still validating or invite is invalid */}
        {!inviteValidating && !(inviteToken && !inviteData) && (
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* General Error */}
            {errors.general && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-red-50 border-2 border-red-500 text-red-700 p-3 rounded font-[var(--font-dm-sans)] text-sm"
              >
                {errors.general}
              </motion.div>
            )}

            {/* Success Message */}
            {success && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-green-50 border-2 border-green-500 text-green-700 p-3 rounded font-[var(--font-dm-sans)] text-sm"
              >
                {success}
                {success.includes('check your email') && (
                  <div className="mt-3">
                    <button
                      type="button"
                      onClick={handleResendConfirmation}
                      className="text-sm bg-green-600 text-white px-3 py-1 rounded hover:bg-green-700 transition-colors"
                    >
                      Resend Confirmation Email
                    </button>
                  </div>
                )}
              </motion.div>
            )}

            {/* Name Fields (Signup and Profile) */}
            {(isSignUp || showProfileForm) && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label htmlFor="firstName" className="block text-sm font-bold text-foreground font-[var(--font-dm-sans)]">
                    First Name
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                    <input
                      type="text"
                      id="firstName"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      className={`w-full pl-12 pr-4 py-3 border-2 ${errors.firstName ? 'border-red-500' : 'border-black'} rounded font-[var(--font-dm-sans)] focus:outline-none focus:ring-0 focus:border-[#0395fc] transition-colors duration-200`}
                      placeholder="First name"
                    />
                  </div>
                  {errors.firstName && (
                    <motion.p initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="text-red-500 text-sm font-[var(--font-dm-sans)]">
                      {errors.firstName}
                    </motion.p>
                  )}
                </div>

                <div className="space-y-2">
                  <label htmlFor="lastName" className="block text-sm font-bold text-foreground font-[var(--font-dm-sans)]">
                    Last Name
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                    <input
                      type="text"
                      id="lastName"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      className={`w-full pl-12 pr-4 py-3 border-2 ${errors.lastName ? 'border-red-500' : 'border-black'} rounded font-[var(--font-dm-sans)] focus:outline-none focus:ring-0 focus:border-[#0395fc] transition-colors duration-200`}
                      placeholder="Last name"
                    />
                  </div>
                  {errors.lastName && (
                    <motion.p initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="text-red-500 text-sm font-[var(--font-dm-sans)]">
                      {errors.lastName}
                    </motion.p>
                  )}
                </div>
              </div>
            )}

            {/* Email Field */}
            <div className="space-y-2">
              <label htmlFor="email" className="block text-sm font-bold text-foreground font-[var(--font-dm-sans)]">
                Email Address
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  readOnly={isSignUp} // Email locked to invite email
                  className={`w-full pl-12 pr-4 py-3 border-2 ${errors.email ? 'border-red-500' : 'border-black'} rounded font-[var(--font-dm-sans)] focus:outline-none focus:ring-0 focus:border-[#0395fc] transition-colors duration-200 ${isSignUp ? 'bg-gray-50 text-muted-foreground cursor-not-allowed' : ''}`}
                  placeholder="your.email@example.com"
                />
              </div>
              {isSignUp && (
                <p className="text-xs text-muted-foreground font-[var(--font-dm-sans)]">Email is set by your invite and cannot be changed.</p>
              )}
              {errors.email && (
                <motion.p initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="text-red-500 text-sm font-[var(--font-dm-sans)]">
                  {errors.email}
                </motion.p>
              )}
            </div>

            {/* Password Field */}
            <div className="space-y-2">
              <label htmlFor="password" className="block text-sm font-bold text-foreground font-[var(--font-dm-sans)]">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  className={`w-full pl-12 pr-12 py-3 border-2 ${errors.password ? 'border-red-500' : 'border-black'} rounded font-[var(--font-dm-sans)] focus:outline-none focus:ring-0 focus:border-[#0395fc] transition-colors duration-200`}
                  placeholder="Enter your password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors duration-200"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              {errors.password && (
                <motion.p initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="text-red-500 text-sm font-[var(--font-dm-sans)]">
                  {errors.password}
                </motion.p>
              )}
            </div>

            {/* Confirm Password (signup only) */}
            {isSignUp && !showProfileForm && (
              <div className="space-y-2">
                <label htmlFor="confirmPassword" className="block text-sm font-bold text-foreground font-[var(--font-dm-sans)]">
                  Confirm Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    id="confirmPassword"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    className={`w-full pl-12 pr-12 py-3 border-2 ${errors.confirmPassword ? 'border-red-500' : 'border-black'} rounded font-[var(--font-dm-sans)] focus:outline-none focus:ring-0 focus:border-[#0395fc] transition-colors duration-200`}
                    placeholder="Confirm your password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors duration-200"
                  >
                    {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                {errors.confirmPassword && (
                  <motion.p initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="text-red-500 text-sm font-[var(--font-dm-sans)]">
                    {errors.confirmPassword}
                  </motion.p>
                )}
              </div>
            )}

            {/* Profile-specific Fields */}
            {showProfileForm && (
              <>
                <div className="space-y-2">
                  <label htmlFor="studentId" className="block text-sm font-bold text-foreground font-[var(--font-dm-sans)]">
                    Student ID
                  </label>
                  <input
                    type="text"
                    id="studentId"
                    name="studentId"
                    value={formData.studentId}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 border-2 ${errors.studentId ? 'border-red-500' : 'border-black'} rounded font-[var(--font-dm-sans)] focus:outline-none focus:ring-0 focus:border-[#0395fc] transition-colors duration-200`}
                    placeholder="Enter your student ID"
                  />
                  {errors.studentId && (
                    <motion.p initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="text-red-500 text-sm font-[var(--font-dm-sans)]">
                      {errors.studentId}
                    </motion.p>
                  )}
                </div>

                <div className="space-y-2">
                  <label htmlFor="program" className="block text-sm font-bold text-foreground font-[var(--font-dm-sans)]">
                    Program
                  </label>
                  <select
                    id="program"
                    name="program"
                    value={formData.program}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 border-2 ${errors.program ? 'border-red-500' : 'border-black'} rounded font-[var(--font-dm-sans)] focus:outline-none focus:ring-0 focus:border-[#0395fc] transition-colors duration-200`}
                  >
                    <option value="">Select your program</option>
                    <option value="Computer Science">Computer Science</option>
                    <option value="Data Science">Data Science</option>
                    <option value="Machine Learning">Machine Learning</option>
                    <option value="Software Engineering">Software Engineering</option>
                    <option value="Information Technology">Information Technology</option>
                    <option value="Other">Other</option>
                  </select>
                  {errors.program && (
                    <motion.p initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="text-red-500 text-sm font-[var(--font-dm-sans)]">
                      {errors.program}
                    </motion.p>
                  )}
                </div>

                <div className="space-y-2">
                  <label htmlFor="graduationYear" className="block text-sm font-bold text-foreground font-[var(--font-dm-sans)]">
                    Expected Graduation Year
                  </label>
                  <input
                    type="number"
                    id="graduationYear"
                    name="graduationYear"
                    value={formData.graduationYear}
                    onChange={handleInputChange}
                    min="2024"
                    max="2030"
                    className="w-full px-4 py-3 border-2 border-black rounded font-[var(--font-dm-sans)] focus:outline-none focus:ring-0 focus:border-[#0395fc] transition-colors duration-200"
                    placeholder="2025"
                  />
                </div>
              </>
            )}

            {/* Submit Button */}
            <motion.button
              type="submit"
              disabled={isLoading}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className={`w-full bg-[#0395fc] text-white py-3 px-6 border-2 border-black rounded font-black font-[var(--font-anton)] text-lg shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] hover:translate-x-[2px] hover:translate-y-[2px] transition-all duration-200 flex items-center justify-center gap-2 ${isLoading ? 'opacity-70 cursor-not-allowed' : ''}`}
            >
              {isLoading ? (
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                  className="w-6 h-6 border-2 border-white border-t-transparent rounded-full"
                />
              ) : showProfileForm ? (
                <>COMPLETE PROFILE <UserPlus className="w-5 h-5" /></>
              ) : isSignUp ? (
                <>CREATE ACCOUNT <UserPlus className="w-5 h-5" /></>
              ) : (
                <>SIGN IN <ArrowRight className="w-5 h-5" /></>
              )}
            </motion.button>
          </form>
        )}

        {/* Footer */}
        <div className="mt-6 text-center">
          <p className="text-xs text-muted-foreground font-[var(--font-dm-sans)]">
            Need help? Contact{' '}
            <a href="mailto:admin@dmls.ca" className="text-[#0395fc] hover:underline font-bold">
              admin@dmls.ca
            </a>
          </p>
        </div>
      </motion.div>
    </div>
  );
};

export default Login;
